import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { cn } from "@/lib/utils";
import { Agent } from "@/types/agent/agent";
import { resolveGenieBrand } from "./genieBrand";

interface GenieSwitcherProps {
  genies: Agent[];
  selectedGenieId: string | null;
  onSelect: (genieId: string) => void;
}

/**
 * Horizontal genie switcher for the dashboard identity header (HG-1229).
 *
 * Only rendered when the account has more than one genie. Each chip is the
 * genie's real avatar in its own brand colour, so switching visibly re-tints
 * the page. Deliberately lighter than `GenieHorizontalSelector` (the knowledge
 * base one), which is coupled to per-genie document counts.
 */
export const GenieSwitcher = ({ genies, selectedGenieId, onSelect }: GenieSwitcherProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const updateScrollControls = () => {
      setCanScrollLeft(scrollContainer.scrollLeft > 4);
      setCanScrollRight(
        scrollContainer.scrollLeft < scrollContainer.scrollWidth - scrollContainer.clientWidth - 4,
      );
    };

    updateScrollControls();
    scrollContainer.addEventListener("scroll", updateScrollControls, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollControls);
    resizeObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener("scroll", updateScrollControls);
      resizeObserver.disconnect();
    };
  }, [genies]);

  const scrollGenies = (showPrevious: boolean) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.scrollBy({
      left: (showPrevious ? -1 : 1) * Math.max(scrollContainer.clientWidth * 0.65, 240),
      behavior: "smooth",
    });
  };

  if (genies.length < 2) return null;

  return (
    <div className="relative">
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollGenies(true)}
          aria-label="Show previous Genies"
          className="absolute left-0 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-md transition-colors hover:text-genie-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary sm:flex"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollGenies(false)}
          aria-label="Show more Genies"
          className="absolute right-0 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-md transition-colors hover:text-genie-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary sm:flex"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className={cn(
          "scrollbar-hidden -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0",
          canScrollLeft && "sm:pl-10",
          canScrollRight && "sm:pr-10",
        )}
        role="tablist"
        aria-label="Switch Genie"
      >
        <div className="flex min-w-min gap-2 py-1">
          {genies.map((genie) => {
            const brand = resolveGenieBrand(genie);
            const isSelected = genie.id === selectedGenieId;

            return (
              <button
                key={genie.id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => onSelect(genie.id)}
                className={cn(
                  "flex flex-shrink-0 items-center gap-2 rounded-full border bg-white py-1.5 pl-1.5 pr-4 transition-all duration-300",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2",
                  isSelected
                    ? "shadow-md"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm",
                )}
                style={
                  isSelected
                    ? { borderColor: brand.primary, backgroundColor: `${brand.primary}0d` }
                    : undefined
                }
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full">
                  <GenieAvatar
                    size={32}
                    color={brand.primary}
                    secondaryColor={brand.secondary}
                    gradientEnabled={brand.gradientEnabled}
                    gradientAngle={brand.gradientAngle}
                    customAvatarUrl={brand.logoUrl}
                    hasCustomBranding={brand.hasCustomBranding}
                  />
                </span>
                <span
                  className="max-w-[10rem] truncate text-sm font-semibold"
                  style={{ color: isSelected ? brand.primary : "#222B45" }}
                >
                  {genie.name || "Untitled Genie"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GenieSwitcher;
